import React, { useState, useEffect } from 'react';
import { Plus, TrendingUp } from 'lucide-react';
import { vitalsService, VitalsRecord } from '../../../lib/services.ipd';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface VitalsTabProps {
  admissionId: string;
}

export default function VitalsTab({ admissionId }: VitalsTabProps) {
  const [vitals, setVitals] = useState<VitalsRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [view, setView] = useState<'table' | 'chart'>('table');
  
  const { register, handleSubmit, reset, formState: { errors } } = useForm<{
    temperature: number;
    pulse: number;
    blood_pressure: string;
    respiration: number;
    spo2: number;
    notes: string;
  }>();

  useEffect(() => {
    loadVitals();
  }, [admissionId]);

  const loadVitals = async () => {
    try {
      const data = await vitalsService.getByAdmission(admissionId);
      setVitals(data);
    } catch (error) {
      console.error('Failed to load vitals:', error);
      toast.error('Failed to load vitals');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: any) => {
    try {
      await vitalsService.create({
        admission_id: admissionId,
        recorded_at: new Date().toISOString(),
        temperature: data.temperature ? parseFloat(data.temperature) : undefined,
        pulse: data.pulse ? parseInt(data.pulse) : undefined,
        blood_pressure: data.blood_pressure || undefined,
        respiration: data.respiration ? parseInt(data.respiration) : undefined,
        spo2: data.spo2 ? parseInt(data.spo2) : undefined,
        notes: data.notes || undefined,
        nurse_id: '',
      });
      toast.success('Vitals recorded successfully');
      reset();
      setShowAddForm(false);
      loadVitals();
    } catch (error) {
      console.error('Failed to record vitals:', error);
      toast.error('Failed to record vitals');
    }
  };

  const chartData = vitals
    .slice()
    .reverse()
    .map((v) => ({
      time: new Date(v.recorded_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      temperature: v.temperature,
      pulse: v.pulse,
      spo2: v.spo2,
    }));

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <h3 className="text-lg font-semibold text-gray-900">Vitals Chart</h3>
          <div className="flex gap-2">
            <button
              onClick={() => setView('table')}
              className={`px-3 py-1 text-sm rounded-lg ${
                view === 'table'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Table
            </button>
            <button
              onClick={() => setView('chart')}
              className={`px-3 py-1 text-sm rounded-lg ${
                view === 'chart'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <TrendingUp className="w-4 h-4 inline mr-1" />
              Graph
            </button>
          </div>
        </div>
        <button
          onClick={() => {
            setShowAddForm(!showAddForm);
            reset();
          }}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="w-4 h-4" />
          Record Vitals
        </button>
      </div>

      {showAddForm && (
        <form onSubmit={handleSubmit(onSubmit)} className="bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Temperature (°F)
              </label>
              <input
                type="number"
                step="0.1"
                {...register('temperature')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="98.6"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pulse (bpm)
              </label>
              <input
                type="number"
                {...register('pulse')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="72"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Blood Pressure
              </label>
              <input
                type="text"
                {...register('blood_pressure')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="120/80"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Respiration (rpm)
              </label>
              <input
                type="number"
                {...register('respiration')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="16"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SpO2 (%)
              </label>
              <input
                type="number"
                {...register('spo2')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="98"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <input
                type="text"
                {...register('notes')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Optional notes"
              />
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Save Vitals
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAddForm(false);
                reset();
              }}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {view === 'chart' && vitals.length > 0 && (
        <div className="bg-white p-4 rounded-lg border border-gray-200">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <Tooltip />
              <Legend />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="temperature"
                stroke="#ef4444"
                name="Temperature (°F)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="pulse"
                stroke="#3b82f6"
                name="Pulse (bpm)"
              />
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="spo2"
                stroke="#10b981"
                name="SpO2 (%)"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {view === 'table' && (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Temp (°F)</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Pulse</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">BP</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resp</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SpO2</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Notes</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vitals.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                    No vitals recorded yet
                  </td>
                </tr>
              ) : (
                vitals.map((vital) => (
                  <tr key={vital.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {new Date(vital.recorded_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {vital.temperature || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {vital.pulse || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {vital.blood_pressure || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {vital.respiration || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {vital.spo2 ? `${vital.spo2}%` : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {vital.notes || '-'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
